import { Box, Container, Grid, Typography, Link } from "@mui/material";
import SearchCat from "~/images/searchCat.svg";
import {
  useGetAggregateScoresRefreshInfoQuery,
  SearchItemStatus,
} from "~/operations";
import { ScopeType } from "~/hooks/useScope";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useCallback, useState } from "react";
import { useLoadScope } from "~/pages/space/home-dashboard/hooks/useLoadScope";
import { SearchHeader } from "./SearchHeader";
import { getCveOrgHref } from "~/pages/Search/utils";

interface CveSearchEmptyStateProps {
  searchQuery: string;
  name: string;
  status?: SearchItemStatus | undefined;
  space?: {
    id: string;
    mrn: string;
    name: string;
  };
  scope?: {
    type: ScopeType;
    mrn: string;
    name: string;
  };
}

export function CveSearchResultsEmptyState({
  searchQuery,
  name,
  status,
  space,
  scope,
}: CveSearchEmptyStateProps) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [newSearchQuery, setNewSearchQuery] = useState(searchQuery);
  const isSpaceDashboard = scope?.type === ScopeType.Space;
  const { assetCount } = useLoadScope();

  const { data: refreshInfoData } = useGetAggregateScoresRefreshInfoQuery({
    variables: {
      groupMrn: space?.mrn || "",
    },
    skip: !isSpaceDashboard || !space?.mrn,
  });

  const lastUpdatedTime =
    refreshInfoData?.aggregateScoresRefreshInfo.lastUpdatedAt || "";

  const handleSearch = useCallback(
    (event: React.FormEvent) => {
      event.preventDefault();
      if (newSearchQuery.trim()) {
        const currentParams = new URLSearchParams(searchParams);
        currentParams.set("q", newSearchQuery.trim());
        navigate(`/space/search?${currentParams.toString()}`);
      }
    },
    [newSearchQuery, searchParams, navigate],
  );

  const cveOrgHref = getCveOrgHref(searchQuery);

  return (
    <Container
      maxWidth="lg"
      sx={{
        pt: 2,
      }}
    >
      <SearchHeader
        title={`${scope?.name || name} search results`}
        query={newSearchQuery}
        onQueryChange={setNewSearchQuery}
        onSubmit={handleSearch}
        lastUpdated={isSpaceDashboard ? lastUpdatedTime : undefined}
      />

      {/* Main Content */}
      <Grid
        container
        alignItems="center"
        gap={1}
        justifyContent="center"
        flexDirection={{ xs: "column", sm: "row" }}
      >
        <Box
          sx={{
            width: 300,
            height: 300,
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Box
            component="img"
            src={SearchCat}
            alt="Search Cat"
            sx={{ width: 217, height: "auto" }}
          />
        </Box>

        <Grid
          size={{
            xs: 12,
            sm: 6,
          }}
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            gap: 2,
          }}
        >
          {status === SearchItemStatus.NotAffected && (
            <>
              <Typography variant="h5" component="h3" color="text.primary">
                <strong>We didn’t detect “{searchQuery}” </strong>on any assets
                in {name}.
              </Typography>
              <Typography variant="body2" color="text.primary">
                Mondoo scanned {assetCount} assets for this vulnerability. You
                can learn more <br /> over at{" "}
                <Link
                  href={cveOrgHref}
                  target="_blank"
                  color="inherit"
                  rel="noopener noreferrer"
                  sx={{ textDecoration: "underline" }}
                >
                  CVE.org
                </Link>
                .
              </Typography>
            </>
          )}

          {status === SearchItemStatus.Unsupported && (
            <>
              <Typography variant="h5" component="h3" color="text.primary">
                <strong>
                  We do not currently have a way to detect <br /> “{searchQuery}
                  ”
                </strong>{" "}
                in {name}
              </Typography>
              <Typography variant="body2" color="text.primary">
                You can learn more about this CVE over at{" "}
                <Link
                  href={cveOrgHref}
                  target="_blank"
                  color="inherit"
                  rel="noopener noreferrer"
                  sx={{ textDecoration: "underline" }}
                >
                  CVE.org
                </Link>
                .
              </Typography>
            </>
          )}

          {!status && (
            <>
              <Typography variant="h5" component="h3" color="text.primary">
                <strong>“{searchQuery}” cannot be found.</strong>
              </Typography>
              <Typography variant="body2" color="text.primary">
                This is either because the vulnerability does not exist or we
                haven’t yet added it <br /> to our vulnerability database.
                <br />
                <br />
                You can search{" "}
                <Link
                  href={cveOrgHref}
                  target="_blank"
                  color="inherit"
                  rel="noopener noreferrer"
                  sx={{ textDecoration: "underline" }}
                >
                  CVE.org
                </Link>{" "}
                for more information.
              </Typography>
            </>
          )}
        </Grid>
      </Grid>
    </Container>
  );
}
